import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ssoConsumeToken, getMe } from '../store/authSlice';
import { DoubleRing } from '../components/motion/PageLoader';

const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'https://auth.sentrivox.com';
const SSO_REDIRECT_URI = process.env.REACT_APP_SSO_REDIRECT_URI || 'https://cloud.sentrivox.com/auth/callback';
const PKCE_VERIFIER_KEY = 'sentrivox_sso_pkce_verifier';
const PKCE_STATE_KEY = 'sentrivox_sso_state';

/**
 * OAuth callback for the auth.sentrivox.com redirect+PKCE login (v4 §7).
 *
 * Distinct from SsoLanding.jsx (/sso): that page consumes a token ALREADY
 * handed over by portal.sentrivox.com via URL hash. This page instead
 * receives an authorization `code` from auth.sentrivox.com and performs the
 * actual PKCE code-for-token exchange itself, using the code_verifier this
 * same browser generated before the redirect (Login.jsx's handleSentrivoxSso).
 *
 * The exchange happens directly from the browser (fetch to auth.sentrivox.com/
 * token) rather than via cloud's own backend - this is the standard OAuth
 * "public client" PKCE model (RFC 7636 exists specifically so a client that
 * can't hold a secret, like a SPA, can still prove it's the same party that
 * started the flow). The resulting access_token is stored the same way a
 * normal password login's token already is (authSlice's ssoConsumeToken),
 * so nothing downstream needs to know which path the user came in through.
 */
const SsoAuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
    // Single-use regardless of outcome - a code_verifier or state value must
    // never be reused across attempts.
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_STATE_KEY);

    async function exchange() {
      if (oauthError) {
        throw new Error(`Sentrivox sign-in was not completed (${oauthError}).`);
      }
      if (!code || !state) {
        throw new Error('Missing authorization code or state.');
      }
      if (!verifier || !expectedState) {
        throw new Error('Sign-in session expired. Please try again.');
      }
      // Constant-time comparison isn't needed here: `state` is not a secret
      // (it's transmitted once already, openly, at /authorize) - this is a
      // CSRF check, not a credential check.
      if (state !== expectedState) {
        throw new Error('Sign-in state mismatch. Please try again.');
      }

      const res = await fetch(`${AUTH_SERVICE_URL}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          code_verifier: verifier,
          redirect_uri: SSO_REDIRECT_URI,
          client_id: 'cloud',
        }),
      });
      if (!res.ok) {
        throw new Error('Sentrivox sign-in failed. Please try again.');
      }
      const data = await res.json();
      if (!data.access_token) {
        throw new Error('Sentrivox sign-in failed. Please try again.');
      }
      return data.access_token;
    }

    exchange()
      .then((accessToken) => {
        dispatch(ssoConsumeToken(accessToken));
        return dispatch(getMe()).unwrap();
      })
      .then((data) => {
        const u = data?.user;
        if (!u?.hasCloudAccess) {
          try {
            localStorage.removeItem('cloudToken');
            localStorage.removeItem('cloudUser');
          } catch (_) { /* ignore */ }
          navigate('/login?reason=upgrade-required', { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        try {
          localStorage.removeItem('cloudToken');
          localStorage.removeItem('cloudUser');
        } catch (_) { /* ignore */ }
        setError(err.message || 'Single sign-on failed. Please log in again.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      });
  }, [dispatch, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif',
      color: '#1f2937'
    }}>
      {!error && <div style={{ marginBottom: 16 }}><DoubleRing /></div>}
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
        {error ? error : 'Signing you in…'}
      </div>
      <div style={{ fontSize: 13, color: '#6b7280' }}>
        Establishing secure session with Sentrivox Cloud Security.
      </div>
    </div>
  );
};

export default SsoAuthCallback;
